import { HttpErrorResponse } from '@angular/common/http';

/**
 * Custom error class for frontend error handling.
 *
 * Produces a short, human-readable message from various error sources. Server
 * error bodies that aren't a usable API message — notably HTML 500/error pages
 * (which Angular surfaces as a raw string or as `{ error, text }` after a failed
 * JSON parse) — are never shown verbatim; we fall back to a status-based message
 * instead. Every message is collapsed and capped to {@link MAX_LENGTH} chars.
 */
export class ErrorFrontend extends Error {
  /** Longest message we'll ever surface; longer strings are truncated. */
  static readonly MAX_LENGTH = 300;

  /** HTTP status when the error came from an HttpErrorResponse, else 0. */
  readonly status: number;

  /** The API's machine code (`datos.code` of a `BackendError` body, e.g.
   *  `AGENT_USER_DAILY_CAP`), when it sent one. */
  readonly code: string | null;

  /**
   * Creates a new ErrorFrontend instance
   * @param data - Error data (string, HttpErrorResponse, or object)
   */
  constructor(data: unknown) {
    const { message, status } = ErrorFrontend._resolve(data);
    super(message);
    this.status = status;
    this.code = data instanceof HttpErrorResponse ? ErrorFrontend._code(data.error) : null;

    // Set the prototype explicitly to maintain instanceof checks
    Object.setPrototypeOf(this, ErrorFrontend.prototype);
  }

  private static _resolve(data: unknown): { message: string; status: number } {
    if (typeof data === 'string') {
      return { message: ErrorFrontend._fromBody(data) ?? 'Ocurrió un error.', status: 0 };
    }
    if (data instanceof HttpErrorResponse) {
      const body = data.error instanceof Blob ? ErrorFrontend._blobToText(data.error) : data.error;
      return {
        message: ErrorFrontend._fromBody(body) ?? ErrorFrontend._statusMessage(data.status),
        status: data.status,
      };
    }
    if (data && typeof data === 'object') {
      return {
        message: ErrorFrontend._fromBody(data) ?? 'Ocurrió un error inesperado.',
        status: 0,
      };
    }
    return { message: 'Ocurrió un error inesperado.', status: 0 };
  }

  /** Extract a clean, short message from an error body, or null if none is usable. */
  private static _fromBody(body: unknown): string | null {
    if (body && typeof body === 'object') {
      const obj = body as Record<string, unknown>;
      // Angular sets { error: <SyntaxError>, text: <raw body> } when JSON parsing
      // fails (e.g. an HTML 500 page) — defer to the raw text branch, which
      // rejects HTML.
      if (typeof obj['text'] === 'string' && !('detail' in obj) && !('message' in obj)) {
        return ErrorFrontend._fromBody(obj['text']);
      }
      const candidate = obj['detail'] ?? obj['message'] ?? obj['error'];
      if (typeof candidate === 'string' && candidate.trim()) {
        return ErrorFrontend._clean(candidate);
      }
      // DRF field errors, e.g. { file: ["No file was submitted."] }.
      const nested = ErrorFrontend._firstString(obj);
      return nested ? ErrorFrontend._clean(nested) : null;
    }
    if (typeof body === 'string') {
      const trimmed = body.trim();
      if (!trimmed || ErrorFrontend._looksLikeHtml(trimmed)) return null;
      try {
        return ErrorFrontend._fromBody(JSON.parse(trimmed));
      } catch {
        // Plain text (not JSON, not HTML): use it only if it's reasonably short.
        return trimmed.length <= ErrorFrontend.MAX_LENGTH ? ErrorFrontend._clean(trimmed) : null;
      }
    }
    return null;
  }

  /** `datos.code` of a JSON error body (the backend's `BackendError`). */
  private static _code(body: unknown): string | null {
    if (!body || typeof body !== 'object') return null;
    const datos = (body as Record<string, unknown>)['datos'];
    if (!datos || typeof datos !== 'object') return null;
    const code = (datos as Record<string, unknown>)['code'];
    return typeof code === 'string' && code ? code : null;
  }

  /** First short, non-HTML string found in an object's values (incl. arrays). */
  private static _firstString(obj: Record<string, unknown>): string | null {
    const usable = (v: unknown): v is string =>
      typeof v === 'string' && v.trim().length > 0 && !ErrorFrontend._looksLikeHtml(v);
    for (const value of Object.values(obj)) {
      if (usable(value)) return value;
      if (Array.isArray(value)) {
        const found = value.find(usable);
        if (found) return found as string;
      }
    }
    return null;
  }

  private static _looksLikeHtml(text: string): boolean {
    return /^\s*<(!doctype|html|\?xml|head|body)/i.test(text) || /<\/html>/i.test(text);
  }

  private static _statusMessage(status: number): string {
    if (status === 0) return 'No se pudo conectar con el servidor. Revise su conexión.';
    if (status === 400) return 'La solicitud no es válida.';
    if (status === 401 || status === 403) return 'No tiene permisos para realizar esta acción.';
    if (status === 404) return 'Recurso no encontrado.';
    if (status === 413) return 'El archivo es demasiado grande.';
    if (status >= 500) return `Error del servidor (${status}). Intente nuevamente.`;
    return `Ocurrió un error inesperado (${status}).`;
  }

  /** Collapse whitespace and cap length. */
  private static _clean(text: string): string {
    const collapsed = text.trim().replace(/\s+/g, ' ');
    return collapsed.length > ErrorFrontend.MAX_LENGTH
      ? collapsed.slice(0, ErrorFrontend.MAX_LENGTH - 1) + '…'
      : collapsed;
  }

  /** Decode an error Blob to text (synchronously). Returns '' on failure. */
  private static _blobToText(blob: Blob): string {
    try {
      const xhr = new XMLHttpRequest();
      const url = URL.createObjectURL(blob);
      xhr.overrideMimeType('text/plain; charset=x-user-defined');
      xhr.open('GET', url, false);
      xhr.send();
      const uint8 = Uint8Array.from(xhr.response as string, (c: string) => c.charCodeAt(0));
      const text = new TextDecoder().decode(uint8);
      URL.revokeObjectURL(url);
      return text;
    } catch {
      return '';
    }
  }
}
